import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import Cards from '@cloudscape-design/components/cards'
import Header from '@cloudscape-design/components/header'
import SpaceBetween from '@cloudscape-design/components/space-between'
import StatusIndicator from '@cloudscape-design/components/status-indicator'
import { useCollection } from '@cloudscape-design/collection-hooks'
import { useProviders } from '@/hooks/useProviders'
import { useOutreachQueue, useApproveOutreachItem, useRejectOutreachItem } from '@/hooks/useOutreachQueue'
import { useAccount } from '@/hooks/useAccount'
import type { OutreachQueueItem } from '@/lib/types'
import SenderEmailBadge from '@/components/common/SenderEmailBadge'
import OutreachCard from './OutreachCard'

interface OutreachTabProps {
  clientId: string
}

const BODY_TRUNCATE_LENGTH = 160

function truncateBody(body: string): string {
  if (body.length <= BODY_TRUNCATE_LENGTH) return body
  return `${body.slice(0, BODY_TRUNCATE_LENGTH)}…`
}

// ─── Pending Approval section ─────────────────────────────────────────────────

interface PendingApprovalSectionProps {
  clientId: string
}

function PendingApprovalSection({ clientId }: PendingApprovalSectionProps) {
  const { items, loading } = useOutreachQueue(clientId)
  const { approveOutreachItem, loading: approving } = useApproveOutreachItem()
  const { rejectOutreachItem, loading: rejecting } = useRejectOutreachItem()

  const { items: collectionItems } = useCollection(items, {})

  if (loading) {
    return (
      <Box padding={{ bottom: 'l' }}>
        <StatusIndicator type="loading">Loading pending approvals…</StatusIndicator>
      </Box>
    )
  }

  return (
    <Cards
      header={
        <Header counter={collectionItems.length > 0 ? `(${collectionItems.length})` : undefined}>
          Pending Approval
        </Header>
      }
      items={collectionItems}
      empty={
        <Box textAlign="center" padding="l">
          <Box variant="p">No outreach emails pending approval.</Box>
        </Box>
      }
      cardDefinition={{
        header: (item: OutreachQueueItem) => item.providerName,
        sections: [
          {
            id: 'touch',
            header: 'Sequence',
            content: (item: OutreachQueueItem) => `Touch ${item.sequencePosition}`,
          },
          {
            id: 'subject',
            header: 'Subject',
            content: (item: OutreachQueueItem) => item.subject,
          },
          {
            id: 'body',
            header: 'Body',
            content: (item: OutreachQueueItem) => truncateBody(item.body),
          },
          {
            id: 'actions',
            content: (item: OutreachQueueItem) => (
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  variant="primary"
                  disabled={approving || rejecting}
                  onClick={() => { void approveOutreachItem(clientId, item.id) }}
                >
                  Approve
                </Button>
                <Button
                  variant="normal"
                  disabled={approving || rejecting}
                  onClick={() => { void rejectOutreachItem(clientId, item.id) }}
                >
                  Reject
                </Button>
              </SpaceBetween>
            ),
          },
        ],
      }}
    />
  )
}

// ─── Main tab ────────────────────────────────────────────────────────────────

export default function OutreachTab({ clientId }: OutreachTabProps) {
  const { providers, loading } = useProviders(clientId)
  const { account } = useAccount()
  const contactedProviders = providers.filter(p => p.status === 'contacted')

  return (
    <SpaceBetween size="l">
      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
        <Header variant="h2">Outreach</Header>
        <SenderEmailBadge
          platforms={account?.platforms ?? []}
          defaultEmailProvider={account?.defaultEmailProvider}
        />
      </SpaceBetween>
      <PendingApprovalSection clientId={clientId} />

      {loading ? (
        <StatusIndicator type="loading">Loading providers…</StatusIndicator>
      ) : contactedProviders.length === 0 ? (
        <Box textAlign="center" padding="xxl">
          <Box variant="p">No contacted providers yet. Update a provider's status to "contacted" to begin outreach.</Box>
        </Box>
      ) : (
        <Cards
          items={contactedProviders}
          cardDefinition={{
            header: (item) => item.provider_name,
            sections: [
              {
                id: 'content',
                content: (item) => (
                  <OutreachCard clientId={clientId} provider={item} />
                ),
              },
            ],
          }}
        />
      )}
    </SpaceBetween>
  )
}
